import { useMemo, useState } from "react";
import { Link } from "wouter";
import { BookOpen, ChevronDown, CircleCheck, Hourglass, XCircle } from "lucide-react";
import { V2_FEATURES } from "@shared/constants/features";
import { STORY_REVIEW_NOTE_MAX } from "@shared/validation/stories.schema";
import type { AdminStorySubmission, StorySubmissionStatus } from "@shared/types/stories";
import { Button } from "../../../components/ui/Button";
import { Card } from "../../../components/ui/Card";
import { DialogShell } from "../../../components/ui/DialogShell";
import { EmptyState } from "../../../components/ui/EmptyState";
import { PageLoader } from "../../../components/ui/Spinner";
import { cn } from "../../../lib/cn";
import { FOCUS_RING } from "../../../lib/focusRing";
import { INPUT_CLASS } from "../../../lib/forms/inputClass";
import { useToast } from "../../../lib/toast/ToastProvider";
import { FEATURE_ACCENT } from "../categories";
import { parseStoryBody } from "../format";
import { SampleChip } from "../components/SampleChip";
import { StoryBody } from "../components/StoryBody";
import { useAdminStorySubmissions, useReviewStory, type ReviewAction } from "./api";

const FILTERS: readonly { id: StorySubmissionStatus; label: string; icon: typeof Hourglass }[] = [
  { id: "submitted", label: "Waiting for review", icon: Hourglass },
  { id: "published", label: "Published", icon: CircleCheck },
  { id: "rejected", label: "Not published", icon: XCircle },
];

function fmt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

interface PendingReview {
  submission: AdminStorySubmission;
  action: ReviewAction;
}

/**
 * Platform console → Stories: members' submissions. Publish (with an optional
 * note to the writer) or reject with a note; a published story can be taken
 * down the same way. Accounts and the story text only — never child records.
 */
export function AdminStoriesPanel() {
  const [status, setStatus] = useState<StorySubmissionStatus>("submitted");
  const [pending, setPending] = useState<PendingReview | null>(null);
  const [note, setNote] = useState("");
  const query = useAdminStorySubmissions(status);
  const review = useReviewStory();
  const { publish } = useToast();

  if (!V2_FEATURES.stories) {
    return (
      <Card className="p-6 text-sm text-neutral-700">
        Stories are switched off (V2_FEATURES.stories in shared/constants/features.ts).
      </Card>
    );
  }

  const open = (submission: AdminStorySubmission, action: ReviewAction) => {
    setNote("");
    setPending({ submission, action });
  };

  const noteRequired = pending?.action === "reject";
  const confirm = () => {
    if (!pending || (noteRequired && !note.trim())) return;
    const { submission, action } = pending;
    review.mutate(
      { id: submission.id, action, note },
      {
        onSuccess: () => {
          setPending(null);
          publish({
            variant: "success",
            title:
              action === "publish"
                ? "Published — it's live in Stories"
                : submission.status === "published"
                  ? "Taken down"
                  : "Rejected — the writer will see your note",
          });
        },
        onError: (err) =>
          publish({
            variant: "error",
            title: err.code === "CONFLICT" ? "Someone already reviewed this one" : "Couldn't save the review",
            description: err.message,
          }),
      },
    );
  };

  const list = query.data ?? [];

  return (
    <div className="space-y-4">
      <div role="group" aria-label="Filter submissions" className="flex flex-wrap gap-2">
        {FILTERS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            aria-pressed={status === id}
            onClick={() => setStatus(id)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition",
              FOCUS_RING,
              status === id
                ? "bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-soft"
                : "bg-white/80 text-neutral-700 ring-1 ring-white hover:bg-white hover:text-sky-700",
            )}
          >
            <Icon className="h-4 w-4" aria-hidden />
            {label}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <PageLoader label="Loading stories…" />
      ) : query.error ? (
        <Card className="flex flex-wrap items-center justify-between gap-3 p-6 text-sm text-red-700">
          Couldn&apos;t load submissions.
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Card>
      ) : list.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title={status === "submitted" ? "Nothing waiting for review" : "Nothing here yet"}
          description={
            status === "submitted"
              ? "New family stories and other submissions will appear here, oldest first."
              : "Reviewed stories will appear here."
          }
        />
      ) : (
        <ul className="space-y-3">
          {list.map((submission) => (
            <li key={submission.id}>
              <SubmissionCard submission={submission} onReview={open} />
            </li>
          ))}
        </ul>
      )}

      <DialogShell
        open={pending !== null}
        onOpenChange={(next) => {
          if (!next && !review.isPending) setPending(null);
        }}
        title={
          pending?.action === "publish"
            ? "Publish this story?"
            : pending?.submission.status === "published"
              ? "Take this story down?"
              : "Reject this story?"
        }
        description={
          pending?.action === "publish"
            ? "It goes live in Stories for every reader in its market. The note is optional and only the writer sees it."
            : "The writer sees your note on their Stories page. Be kind, and say what would make it publishable if you can."
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirm();
          }}
        >
          <label htmlFor="story-review-note" className="mt-4 block text-sm font-medium text-neutral-800">
            {noteRequired ? "Note to the writer (required)" : "Note to the writer (optional)"}
          </label>
          <textarea
            id="story-review-note"
            rows={4}
            maxLength={STORY_REVIEW_NOTE_MAX}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={cn(INPUT_CLASS, "mt-1 resize-y")}
            placeholder={
              noteRequired
                ? "e.g. Thank you for sharing this. Could you take out the hospital's name and your son's full name, then send it again?"
                : "e.g. Thank you — this is going to help so many parents."
            }
            aria-describedby={noteRequired ? "story-review-note-hint" : undefined}
          />
          {noteRequired && (
            <p id="story-review-note-hint" className="mt-1 text-xs text-neutral-600">
              A short, kind reason is needed.
            </p>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setPending(null)} disabled={review.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={pending?.action === "reject" ? "destructive" : "primary"}
              isLoading={review.isPending}
              disabled={noteRequired && !note.trim()}
            >
              {review.isPending
                ? "Working…"
                : pending?.action === "publish"
                  ? "Publish"
                  : pending?.submission.status === "published"
                    ? "Take down"
                    : "Reject"}
            </Button>
          </div>
        </form>
      </DialogShell>
    </div>
  );
}

function SubmissionCard({
  submission,
  onReview,
}: {
  submission: AdminStorySubmission;
  onReview: (submission: AdminStorySubmission, action: ReviewAction) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const blocks = useMemo(() => (expanded ? parseStoryBody(submission.body) : []), [expanded, submission.body]);
  const bodyId = `admin-story-${submission.id}`;

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {submission.category}
            {submission.isSample && <SampleChip />}
          </p>
          <h3 className="mt-1 font-display text-lg font-semibold text-neutral-900">{submission.title}</h3>
          <p className="mt-1 text-sm text-neutral-600">
            by <span className="font-medium text-neutral-900">{submission.author.displayName}</span> ·{" "}
            {submission.author.email} · {submission.wordCount} words · sent {fmt(submission.submittedAt)}
          </p>
          {submission.authorBio && (
            <p className="mt-1 text-sm text-neutral-600">&ldquo;{submission.authorBio}&rdquo;</p>
          )}
          {submission.reviewNote && (
            <p className="mt-2 rounded-xl bg-sky-50/80 px-3 py-2 text-sm text-neutral-700">
              <span className="font-medium">Note to writer:</span> {submission.reviewNote}
              {submission.reviewedAt && (
                <span className="text-neutral-500"> · {fmt(submission.reviewedAt)}</span>
              )}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {submission.status === "submitted" && (
            <>
              <Button size="sm" onClick={() => onReview(submission, "publish")}>
                Publish
              </Button>
              <Button size="sm" variant="secondary" onClick={() => onReview(submission, "reject")}>
                Reject
              </Button>
            </>
          )}
          {submission.status === "published" && (
            <>
              {submission.slug && (
                <Button asChild size="sm" variant="secondary">
                  <Link href={`/stories/${submission.slug}`}>Read live</Link>
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onReview(submission, "reject")}>
                Take down
              </Button>
            </>
          )}
        </div>
      </div>

      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          "mt-3 inline-flex items-center gap-1 rounded-full text-sm font-semibold text-sky-700 hover:text-sky-900",
          FOCUS_RING,
        )}
      >
        {expanded ? "Hide the story" : "Read the story"}
        <ChevronDown className={cn("h-4 w-4 transition", expanded && "rotate-180")} aria-hidden />
      </button>
      {expanded && (
        <div id={bodyId} className="mt-3 rounded-2xl bg-white px-4 py-4 ring-1 ring-sky-100 sm:px-6">
          <StoryBody blocks={blocks} accent={FEATURE_ACCENT[submission.category]} />
        </div>
      )}
    </Card>
  );
}
